/**
 * LandingSEO — per-route SEO + GEO metadata for the Lumfi landing.
 *
 * Why a dedicated component
 * -------------------------
 * The static baseline in ``public/index.html`` gives crawlers a strong
 * first impression *before* JS even runs. This component layers on top
 * of it with locale-aware overrides (title/description/OG) so that:
 *
 *   • A user reaching ``/?lang=pt`` sees the Portuguese title in
 *     their tab and in any shared link preview.
 *   • Google Search Console and Bing receive a single canonical with
 *     hreflang siblings — avoiding duplicate-content penalties.
 *   • LLM-driven search engines (ChatGPT, Perplexity, Gemini) get a
 *     ``FAQPage`` schema with the same Q&A the user reads on-page,
 *     greatly increasing the odds of being cited verbatim — that's
 *     the core of GEO (Generative Engine Optimisation).
 *
 * Usage
 * -----
 *   <LandingSEO lang="es" country="CO" />
 *
 * The component is pure metadata — it renders nothing visible.
 */
import { Helmet } from "react-helmet-async";
import { tx } from "../../i18n";

const SITE = "https://lumfi.wintimeapp.com";

const COPY = {
  es: {
    title: "Lumfi Cash Pilot · Sabé cuánto puedes gastar esta semana",
    description: "App de flujo de caja para freelancers de LATAM con ingresos variables. Te dice tu plata real, el límite seguro semanal y el riesgo del mes. Registra movimientos por chat, Telegram o WhatsApp.",
    locale: "es_CO",
  },
  en: {
    title: "Lumfi Cash Pilot · Know how much you can spend this week",
    description: "Cash-flow app for LATAM freelancers with variable income. Shows your real cash, safe weekly limit and monthly risk. Log movements by chat, Telegram or WhatsApp.",
    locale: "en_US",
  },
  pt: {
    title: "Lumfi Cash Pilot · Saiba quanto pode gastar esta semana",
    description: "App de fluxo de caixa para freelancers da LATAM com renda variável. Mostra seu caixa real, limite semanal seguro e risco do mês. Registre movimentos por chat, Telegram ou WhatsApp.",
    locale: "pt_BR",
  },
};

// FAQ content used both for the visible accordion and for the
// JSON-LD ``FAQPage`` schema. Kept in this file so it's the single
// source of truth — when marketing updates the FAQ, the structured
// data updates too.
const FAQS_ES = [
  {
    q: "¿Qué es Lumfi y para quién está pensado?",
    a: "Lumfi es una app de flujo de caja diseñada específicamente para freelancers con ingresos variables en LATAM. Te muestra tu plata real disponible, cuánto puedes gastar esta semana sin descalabrarte y qué tan riesgoso luce el mes próximo. Útil si trabajas por proyecto, cobras cuando puedes y tienes meses buenos y malos.",
  },
  {
    q: "¿Cómo calcula Lumfi el límite seguro semanal?",
    a: "Lumfi cruza tu cash actual, tus cobros esperados con probabilidad de llegar, los pagos comprometidos del mes y tus deudas activas. El resultado es un número honesto: cuánto puedes tocar esta semana sin poner en riesgo lo que viene. No es presupuesto, es realidad.",
  },
  {
    q: "¿En qué países funciona Lumfi?",
    a: "Lumfi soporta nativamente Colombia, México, Argentina, Brasil, Chile, Perú y Uruguay con sus respectivas monedas y conversiones FX automáticas. Si trabajas con clientes en USD pero vives en COP, Lumfi te muestra todo en tu moneda con tasas reales.",
  },
  {
    q: "¿Cómo registro movimientos? ¿Tengo que tipear todo?",
    a: "No. Lumfi tiene un asistente de IA que entiende lenguaje natural. Le dices 'gasté 50 mil en almuerzo' o 'me llegaron 2 millones del cliente X' y lo registra. Funciona desde la web, desde Telegram y desde WhatsApp (con plan Pilot+).",
  },
  {
    q: "¿Cuánto cuesta Lumfi?",
    a: "Hay un plan Control gratuito para arrancar, un plan Pilot con IA + alertas FX desde 29.900 COP / 139 MXN / 11.200 ARS al mes, y un plan Negocio para equipos pequeños.",
  },
  {
    q: "¿Mis datos están seguros?",
    a: "Sí. Lumfi cifra los datos en tránsito y en reposo, no vende información a terceros y no se conecta a tus cuentas bancarias (tu cargas los movimientos manualmente o por el bot). Cumple con buenas prácticas de privacidad LATAM.",
  },
];

const FAQS_EN = [
  {
    q: "What is Lumfi and who is it for?",
    a: "Lumfi is a cash-flow app designed specifically for LATAM freelancers with variable income. It shows your real available cash, how much you can spend this week without going under and how risky next month looks. Useful if you bill by project, get paid when you can and have good and bad months.",
  },
  {
    q: "How does Lumfi compute the safe weekly limit?",
    a: "Lumfi cross-references your current cash, expected receivables weighted by likelihood, committed payments for the month and active debts. The result is an honest number: how much you can touch this week without putting what's coming at risk.",
  },
  {
    q: "Which countries does Lumfi support?",
    a: "Lumfi natively supports Colombia, Mexico, Argentina, Brazil, Chile, Peru and Uruguay with their currencies and automatic FX conversion. If you bill clients in USD but live in COP, Lumfi shows everything in your local currency at real rates.",
  },
  {
    q: "How do I log movements? Do I have to type everything?",
    a: "No. Lumfi has an AI assistant that understands plain language. Just say 'spent 50k on lunch' or 'got paid 2M from client X' and it logs it. Works on web, Telegram and WhatsApp (Pilot+ plan).",
  },
  {
    q: "How much does Lumfi cost?",
    a: "Free Control tier to start. Pilot plan with AI + FX alerts from ~$7 USD/month. Negocio plan for small teams. 14-day free trial without credit card.",
  },
  {
    q: "Is my data safe?",
    a: "Yes. Lumfi encrypts data in transit and at rest, doesn't sell info to third parties and doesn't connect to your bank accounts (you log movements manually or via the bot). Follows LATAM privacy best practices.",
  },
];

const FAQS = { es: FAQS_ES, en: FAQS_EN, pt: FAQS_ES /* pt placeholder = es */ };


export function LandingSEO({ lang = "es", country = "CO" }) {
  const copy = COPY[lang] || COPY.es;
  const url = `${SITE}/${tx(lang, `?lang=${lang}`, "", "")}`;
  const faqs = FAQS[lang] || FAQS.es;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  // Breadcrumb gives search engines + voice assistants the right
  // "you are here" context when ranking the page.
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Lumfi", item: SITE },
    ],
  };

  // Default OG image for the root landing — uses the same Lumfi brand
  // icon as before the per-archetype dynamic OG endpoint moved into
  // Helmet for /for/:slug deep-dives. Origin-aware so preview/staging
  // serve their own copy correctly.
  const ORIGIN = typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : SITE;
  const ogImage = `${ORIGIN}/icons/lumfi-app-icon-1024.png`;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{copy.title}</title>
      <meta name="description" content={copy.description} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:title" content={copy.title} />
      <meta property="og:description" content={copy.description} />
      <meta property="og:url" content={url} />
      <meta property="og:locale" content={copy.locale} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1024" />
      <meta property="og:image:height" content="1024" />

      {/* Twitter */}
      <meta name="twitter:title" content={copy.title} />
      <meta name="twitter:description" content={copy.description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Hint country/lang to GEO engines */}
      <meta name="geo.region" content={country} />
      <meta name="lumfi:active-country" content={country} />

      {/* JSON-LD: FAQ + Breadcrumb (Organization + SoftwareApplication
          live in index.html so crawlers see them pre-hydration) */}
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
    </Helmet>
  );
}

// Exported so the FAQ section can render the same content the JSON-LD
// already declares — keeping the visible page and the structured data
// in sync is a key GEO requirement (Google penalises pages whose
// FAQ schema doesn't match the on-page text).
export { FAQS };
